<template>
  <div class="absolute inset-0">
    <div ref="itemChartCanvasContainer" class="absolute h-full w-full">
      <canvas ref="itemChartCanvas" class="absolute h-full w-full" />
    </div>
  </div>
</template>

<script lang="ts" setup>
import type { DateTime } from 'luxon';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useMousePosition } from '@/hooks/use-mouse-position';
import type {
  BlockInternal,
  CategoryInternal,
  Config,
  GanttHoverContent,
  LineInternal,
} from './interfaces';
import { TRANSFER_JOIN_RADIUS_PX } from './connect-transfer-bars';
import { type Day, getDay } from './utils';

const props = defineProps<{
  configObj: Config;
  categories?: CategoryInternal[];
  lines?: LineInternal[];
  todayDate: DateTime;
  currentScrollPosition: { x: number; y: number };
  sideBarWidth: number;
  isDesktop: boolean;
}>();

const emit = defineEmits<{
  (e: 'onShowHover', content: GanttHoverContent, yPos: number): void;
  (e: 'onHideHover'): void;
}>();

const res = 2;

const dateBarMonthHeight = ref(29 * res);
const dateBarWeekHeight = ref(27 * res);
const dateBarWeekDayHeight = ref(22 * res);
const dateBarDayHeight = ref(22 * res);
const dateBarHeight = computed(
  () =>
    dateBarMonthHeight.value +
    dateBarWeekDayHeight.value +
    dateBarDayHeight.value +
    (props.isDesktop ? dateBarWeekHeight.value : 0),
);

const itemChartLineColor = '#eee';
const categoryBGColor = '#eee';
const blockStandardColor = '#888';
const ganttFont = `${12 * res}px "Figtree Variable"`;
const itemChartTodayColor = '#e6ffdd';
const itemChartWeekendBGColor = '#f7f7f7';
const itemChartFontColor = '#fff';
const dateBarBGColor = '#fff';
const dateBarBorderColor = '#ddd';
const dateBarLineColor = '#ddd';
const dateBarTodayBGColor = '#e6ffdd';
const dateBarWeekendBGColor = '#eee';
const dateBarFontColor = '#000';

const itemChartCanvas = ref();
const itemChartCanvasContainer = ref();

let visibleBlocks: BlockInternal[] = [];
let visibleLines: LineInternal[] = [];
let ctx: CanvasRenderingContext2D | null;
let categoriesWidth: number;
let scaleX: number;
let scaleY: number;
const visibleDayCount = ref<number>();
let canvasScrollPosition: { x: number; y: number };
let currentScrollPositionDays: number;
const firstVisibleDay = ref<DateTime>();
let lastFirstVisibleDay: DateTime;
let calculatedDays: { day: Day; todayDiff: number }[];
let hoveredBlock: BlockInternal | null = null;
let hoveredBlockId: string | null = null;
let hoveredBlockYPos: number | null = null;
let lastCursor: string | null = null;
let canvasRect = { left: 0, top: 0 };
let paintRaf = 0;
let unmounted = false;
let lastPointerX = 0;
let lastPointerY = 0;

const mousePosition = useMousePosition();

const updateCanvasRect = () => {
  if (!itemChartCanvas.value) return;
  const rect = itemChartCanvas.value.getBoundingClientRect();
  canvasRect = { left: rect.left, top: rect.top };
};

const pointerInCanvas = () => ({
  x: (lastPointerX - canvasRect.left) * res,
  y: (lastPointerY - canvasRect.top) * res,
});

let resizeObserver: ResizeObserver | undefined;

onMounted(() => {
  ctx = itemChartCanvas.value.getContext('2d', { alpha: false });
  if (!ctx) return;
  resizeObserver = new ResizeObserver(() => {
    updateCanvasRect();
    schedulePaint();
  });
  resizeObserver.observe(itemChartCanvasContainer.value);
  document.addEventListener('visibilitychange', handleVisibility);
  updateCanvasRect();
  schedulePaint();
  void document.fonts.ready.then(schedulePaint);
});

const handleVisibility = () => {
  if (!document.hidden) schedulePaint();
};

onBeforeUnmount(() => {
  unmounted = true;
  if (paintRaf) cancelAnimationFrame(paintRaf);
  resizeObserver?.disconnect();
  document.removeEventListener('visibilitychange', handleVisibility);
});

const schedulePaint = () => {
  if (unmounted || paintRaf) return;
  paintRaf = requestAnimationFrame(() => {
    paintRaf = 0;
    if (unmounted || document.hidden) return;
    paint();
  });
};

watch(
  () => [
    props.currentScrollPosition.x,
    props.currentScrollPosition.y,
    props.sideBarWidth,
    props.isDesktop,
    props.configObj.scaleX,
    props.configObj.scaleY,
    props.categories,
  ],
  schedulePaint,
);

watch(mousePosition, () => {
  checkBlocksMouseOver();
});

const paint = () => {
  if (!ctx) return;
  calculateRelevantValues(ctx);
  calculateDays();
  calculateBlockPositions(ctx);
  checkBlocksMouseOver();
  drawLines(ctx);
  drawBlocks(ctx);
  drawDateBar(ctx);
};

const calculateRelevantValues = (ctx: CanvasRenderingContext2D) => {
  const cssW = itemChartCanvasContainer.value.offsetWidth;
  const cssH = itemChartCanvasContainer.value.offsetHeight;
  const bufferW = cssW * res;
  const bufferH = cssH * res;
  if (ctx.canvas.width !== bufferW || ctx.canvas.height !== bufferH) {
    ctx.canvas.width = bufferW;
    ctx.canvas.height = bufferH;
    updateCanvasRect();
  }
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, bufferW, bufferH);
  categoriesWidth = props.sideBarWidth * res;
  scaleX = props.configObj.scaleX * res;
  scaleY = props.configObj.scaleY * res;
  const nextVisibleDayCount = Math.ceil(bufferW / scaleX) + 4;
  if (visibleDayCount.value !== nextVisibleDayCount) {
    visibleDayCount.value = nextVisibleDayCount;
  }
  canvasScrollPosition = { ...props.currentScrollPosition };
  canvasScrollPosition.x = canvasScrollPosition.x - cssW / 2;
  currentScrollPositionDays = Math.floor(canvasScrollPosition.x / props.configObj.scaleX - 3);
  const newFirstVisibleDay = props.todayDate
    .minus({
      days: -currentScrollPositionDays,
    })
    .startOf('day');
  if (!firstVisibleDay.value || !firstVisibleDay.value.equals(newFirstVisibleDay)) {
    firstVisibleDay.value = newFirstVisibleDay;
  }
};

const calculateDays = () => {
  if (
    !firstVisibleDay.value ||
    (lastFirstVisibleDay && lastFirstVisibleDay.diff(firstVisibleDay.value).as('days') === 0)
  ) {
    return;
  }
  lastFirstVisibleDay = firstVisibleDay.value;
  const newCalculatedDays = [];
  let currentDay = firstVisibleDay.value;
  for (let i = 0; i < (visibleDayCount.value ?? 0); i++) {
    let newCalculatedDay = calculatedDays?.find(
      (calculatedDay) => calculatedDay.day.dateTime.diff(currentDay).as('days') === 0,
    ) ?? {
      day: getDay(currentDay),
      todayDiff: currentDay.diff(props.todayDate).as('days'),
    };
    newCalculatedDays.push(newCalculatedDay);
    currentDay = currentDay.plus({ day: 1 });
  }
  calculatedDays = newCalculatedDays;
};

const calculateBlockPositions = (ctx: CanvasRenderingContext2D) => {
  const lastById = new Map(
    visibleBlocks.map((block) => [
      block.id,
      { remainingWidth: getRemainingBlockWidth(block), shortenedTitle: block.shortenedTitle },
    ]),
  );
  visibleBlocks = [];
  visibleLines = [];
  if (!props.categories) return;
  let index = 0;
  for (const category of props.categories) {
    index++;
    if (!category.lines || category.hidden) continue;
    for (const line of category.lines) {
      if (line.hiddenByLineType || line.hiddenByTags || !line.blocks) continue;
      const yPos = Math.round(index * scaleY - canvasScrollPosition.y * res + dateBarHeight.value);
      index++;

      // Don't process lines outside the canvas
      if (!(yPos >= -scaleY && yPos <= ctx.canvas.height)) {
        continue;
      }
      visibleLines.push({ ...line, yPos });

      for (const block of line.blocks) {
        const xStartPos =
          block.xStartPosInDays !== null
            ? block.xStartPosInDays * scaleX - canvasScrollPosition.x * res
            : null;
        const xEndPos =
          block.xEndPosIndDays !== null
            ? block.xEndPosIndDays * scaleX - canvasScrollPosition.x * res
            : null;

        block.xPos = xStartPos !== null ? Math.round(xStartPos) : -100;
        block.width =
          xEndPos !== null
            ? Math.round(xEndPos - block.xPos)
            : Math.round(ctx.canvas.width + 100 - block.xPos);
        block.yPos = yPos;

        if (!isVisibleElement(block.xPos, block.yPos, block.width)) continue;

        const lastBlock = lastById.get(block.id);
        const remainingWidth = getRemainingBlockWidth(block);
        const lastRemainingWidth = lastBlock?.remainingWidth ?? null;
        if (lastBlock && lastRemainingWidth === remainingWidth) {
          block.shortenedTitle = lastBlock.shortenedTitle;
        } else {
          ctx.font = ganttFont;
          block.shortenedTitle = shortenTitle(block.title, remainingWidth - 20, ctx);
        }

        visibleBlocks.push(block);
      }
    }
  }
  visibleBlocks.sort((a, b) => (a.tagObj?.zIndex ?? 0) - (b.tagObj?.zIndex ?? 0));
};

const getRemainingBlockWidth = (block: BlockInternal) => {
  const padLeft = block.textPadLeftDays * scaleX;
  const padRight = block.textPadRightDays * scaleX;
  const x = block.xPos + padLeft;
  const width = Math.max(0, block.width - padLeft - padRight);
  const cropped = categoriesWidth - x;
  return Math.min(width, width - cropped);
};

const isVisibleElement = (xPos: number, yPos: number, width: number) => {
  return (
    xPos >= -width &&
    xPos <= itemChartCanvas.value.width &&
    yPos >= scaleY &&
    yPos <= itemChartCanvas.value.height
  );
};

const checkBlocksMouseOver = () => {
  if (!ctx || scaleY == null || !itemChartCanvasContainer.value) return;
  lastPointerX = mousePosition.value.x;
  lastPointerY = mousePosition.value.y;
  const pointer = pointerInCanvas();
  hoveredBlock = null;
  // Global mouse coords still map onto bars under overlays (main menu, etc.).
  const hit = document.elementFromPoint(lastPointerX, lastPointerY);
  if (hit && itemChartCanvasContainer.value.contains(hit) && pointer.x > categoriesWidth) {
    for (let i = visibleBlocks.length - 1; i >= 0; i--) {
      const block = visibleBlocks[i];
      if (!(block.onClick || block.url || block.hover)) continue;
      if (
        pointer.x > block.xPos &&
        pointer.x < block.xPos + block.width &&
        pointer.y > block.yPos &&
        pointer.y < block.yPos + scaleY
      ) {
        hoveredBlock = block;
        break;
      }
    }
  }

  const nextCursor =
    hoveredBlock && (hoveredBlock.onClick || hoveredBlock.url || hoveredBlock.hover)
      ? 'pointer'
      : '';
  if (nextCursor !== lastCursor) {
    lastCursor = nextCursor;
    itemChartCanvasContainer.value.style.cursor = nextCursor || null;
  }

  const nextId = hoveredBlock?.id ?? null;
  const nextYPos = hoveredBlock ? hoveredBlock.yPos / res : null;
  if (nextId === hoveredBlockId && nextYPos === hoveredBlockYPos) return;

  hoveredBlockId = nextId;
  hoveredBlockYPos = nextYPos;

  if (!hoveredBlock?.hover) {
    emit('onHideHover');
    return;
  }
  emit('onShowHover', hoveredBlock.hover(), hoveredBlock.yPos / res);
};

const drawLines = (ctx: CanvasRenderingContext2D) => {
  if (!props.categories || !calculatedDays) return;

  ctx.lineWidth = 1 * res;
  ctx.strokeStyle = itemChartLineColor;
  ctx.beginPath();

  for (const calculatedDay of calculatedDays) {
    const { isWeekend, isToday } = calculatedDay.day;
    const xPos = Math.round(calculatedDay.todayDiff * scaleX - canvasScrollPosition.x * res);

    if (isToday) {
      ctx.fillStyle = itemChartTodayColor;
      ctx.fillRect(xPos, 0, scaleX, ctx.canvas.height);
    } else if (isWeekend) {
      ctx.fillStyle = itemChartWeekendBGColor;
      ctx.fillRect(xPos, 0, scaleX, ctx.canvas.height);
    }

    ctx.moveTo(xPos, 0);
    ctx.lineTo(xPos, ctx.canvas.height);
  }
  ctx.stroke();

  ctx.beginPath();
  let index = 0;
  for (const category of props.categories) {
    drawHorizontalLine(ctx, index, true);
    index++;
    if (!category.lines || category.hidden) continue;
    for (const line of category.lines) {
      drawHorizontalLine(ctx, index, !!line.color, line.color, 0.5);
      index++;
    }
  }
  ctx.stroke();
};

const drawHorizontalLine = (
  ctx: CanvasRenderingContext2D,
  index: number,
  drawBackground = false,
  backgroundColor = categoryBGColor,
  backgroundAlpha = 1,
) => {
  const yPos = Math.round(index * scaleY - canvasScrollPosition.y * res + dateBarHeight.value);
  if (yPos + scaleY < 0 || yPos > ctx.canvas.height) return;

  if (drawBackground) {
    ctx.globalAlpha = backgroundAlpha;
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, yPos, ctx.canvas.width, scaleY);
    ctx.globalAlpha = 1;
  }

  ctx.moveTo(0, yPos);
  ctx.lineTo(ctx.canvas.width, yPos);
};

const drawBlocks = (ctx: CanvasRenderingContext2D) => {
  ctx.font = ganttFont;
  const barH = scaleY - 4 * res;
  const radius = TRANSFER_JOIN_RADIUS_PX * res;
  for (const block of visibleBlocks) {
    ctx.fillStyle = block.color ?? block.tagObj?.color ?? blockStandardColor;
    ctx.globalAlpha = block.color ? 1 : (block.tagObj?.alpha ?? 1);
    const maxR = Math.min(radius, barH / 2, Math.max(block.width, 0) / 2);
    const left = block.roundLeft ? maxR : 0;
    const right = block.roundRight ? maxR : 0;
    ctx.beginPath();
    ctx.roundRect(block.xPos, block.yPos + 2 * res, block.width, barH, [left, right, right, left]);
    ctx.fill();
    ctx.globalAlpha = 1;

    const textPos = Math.max(block.xPos + block.textPadLeftDays * scaleX, categoriesWidth);
    ctx.fillStyle = itemChartFontColor;
    ctx.fillText(block.shortenedTitle, textPos + 7 * res, block.yPos + scaleY - 8 * res);
  }
};

const shortenTitle = (title: string, maxLength: number, ctx: CanvasRenderingContext2D) => {
  if (maxLength <= 0) return '';
  if (ctx.measureText(title).width <= maxLength) return title;
  const ellipsis = ' ...';
  const ellipsisWidth = ctx.measureText(ellipsis).width;
  if (ellipsisWidth >= maxLength) return '';
  let lo = 0;
  let hi = title.length;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (ctx.measureText(title.slice(0, mid)).width + ellipsisWidth <= maxLength) {
      lo = mid;
    } else {
      hi = mid - 1;
    }
  }
  return lo > 0 ? title.slice(0, lo) + ellipsis : '';
};

const drawDateBar = (ctx: CanvasRenderingContext2D) => {
  ctx.lineWidth = 1 * res;
  ctx.font = ganttFont;

  ctx.fillStyle = dateBarBGColor;
  ctx.fillRect(0, 0, ctx.canvas.width, dateBarHeight.value);
  for (const calculatedDay of calculatedDays) {
    const day = calculatedDay.day;
    const xPos = Math.round(calculatedDay.todayDiff * scaleX - canvasScrollPosition.x * res);

    const dayHeight = dateBarDayHeight.value + dateBarWeekDayHeight.value;

    if (day.isToday) {
      ctx.fillStyle = dateBarTodayBGColor;
      ctx.fillRect(xPos, dateBarHeight.value - dayHeight, scaleX, dayHeight);
    } else if (day.isWeekend) {
      ctx.fillStyle = dateBarWeekendBGColor;
      ctx.fillRect(xPos, dateBarHeight.value - dayHeight, scaleX, dayHeight);
    }

    const strokeHeight =
      dayHeight +
      (day.isNewWeek ? dateBarWeekHeight.value : 0) +
      (day.isNewMonth ? dateBarWeekHeight.value + dateBarMonthHeight.value : 0);
    ctx.strokeStyle = dateBarLineColor;
    ctx.beginPath();
    ctx.moveTo(xPos, dateBarHeight.value - strokeHeight);
    ctx.lineTo(xPos, dateBarHeight.value);
    ctx.stroke();

    ctx.fillStyle = dateBarFontColor;
    ctx.fillText(day.monthday, xPos + 6 * res, dateBarHeight.value - 11 * res);
    ctx.fillText(day.weekday, xPos + 6 * res, dateBarHeight.value - 29 * res);
    if (props.isDesktop && day.isNewWeek) {
      ctx.fillText(day.weekString, xPos + 6 * res, dateBarHeight.value - 58 * res);
    }
    if ((day.dateTime.day === 1 || xPos <= 0) && xPos + scaleX > 0) {
      const padding = 6 * res;
      const actualXPos = xPos + padding < padding ? padding : xPos + padding;
      ctx.fillStyle = dateBarBGColor;
      ctx.fillRect(xPos + res, res, scaleX * 27, dateBarMonthHeight.value);
      ctx.fillStyle = dateBarFontColor;
      ctx.fillText(day.monthString, actualXPos, 18 * res);
    }
  }

  ctx.strokeStyle = dateBarBorderColor;
  ctx.beginPath();
  ctx.moveTo(0, dateBarHeight.value - 1 * res);
  ctx.lineTo(ctx.canvas.width, dateBarHeight.value - 1 * res);
  ctx.stroke();
};

/**
 * Apply mouse click on a block element
 * @param showHoverFirst Touch: first tap only opens the hover card, second tap follows the link
 */
const onBlockClick = (showHoverFirst = false) => {
  if (!hoveredBlock) return;
  if (showHoverFirst && hoveredBlock.hover) {
    emit('onShowHover', hoveredBlock.hover(), hoveredBlock.yPos / res);
    return;
  }
  if (!hoveredBlock?.onClick && !hoveredBlock?.url) return;

  if (hoveredBlock.onClick) {
    hoveredBlock.onClick();
  }
  if (hoveredBlock.url) {
    window.location.href = hoveredBlock.url;
  }
};

const onCalendarClick = (callback: (line: LineInternal | undefined, Date: DateTime) => void) => {
  if (hoveredBlock) return;
  const pointer = pointerInCanvas();
  if (pointer.x > categoriesWidth && pointer.y > dateBarHeight.value) {
    const line = visibleLines.find((line) => line.yPos && line.yPos + scaleY >= pointer.y);
    const date = props.todayDate.plus({
      days: (canvasScrollPosition.x + pointer.x / res) / props.configObj.scaleX,
    });
    callback(line, date);
  }
};

const getCanvasHeight = () => ((ctx?.canvas?.height ?? 0) - dateBarHeight.value) / res;

defineExpose({
  loop: schedulePaint,
  onBlockClick,
  onCalendarClick,
  getCanvasHeight,
  dateBarHeight: computed(() => dateBarHeight.value / res),
  firstVisibleDay,
  lastVisibleDay: computed(() =>
    firstVisibleDay.value?.plus({
      days: visibleDayCount.value ?? 0,
    }),
  ),
  visibleDayCount,
});
</script>
